import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, obj = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Listen for the api-client's 401→refresh-failed broadcast. When the
  // refresh-token has itself expired (7 days) any subsequent XHR will
  // fail with 401 and the interceptor dispatches this event. We flip
  // the user object to anonymous so the next render redirects to
  // /login instead of leaving the admin console in a half-broken
  // state where every page shows 'not_authenticated'.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onExpired = () => setUser(false);
    window.addEventListener("veralok:session_expired", onExpired);
    return () => window.removeEventListener("veralok:session_expired", onExpired);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // MFA gate: return the intermediate token to the caller for the challenge step.
    if (data?.mfa_required) return { mfa_required: true, mfa_token: data.mfa_token, email: data.email };
    setUser(data);
    return data;
  };

  const verifyMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token, code });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
